import { ArrowDown, ArrowUp, CalendarDays, DollarSign, FileText, PlusCircle, ShoppingCart, Users, AlertTriangle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";
import MeetingsAPI, { ReviewablePendingBookDto } from "../../api/MeetingsAPI";
import StatisticsAPI from "../../api/StatisticsAPI";
import HomepageAdsAPI, { LiveHomepageAd } from "../../api/HomepageAdsAPI";
import { AdminRestricted } from "../../components/AdminRestricted/AdminRestricted";
import BannerSlider, { BannerData } from "../../components/BannerSlider/BannerSlider";
import HomeAdModal from "../../components/HomeAdModal/HomeAdModal";
import { CardContent, CardDescription, CardHeader, CardTitle, CardV2 } from "../../components/CardV2/CardV2";
import { Link } from "../../components/Link/Link";
import { PermissionFlags } from "../../components/PermissionFlags/PermissionFlags";
import PlainTextButton from "../../components/PlainTextButton/PlainTextButton";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { useGetOrders } from "../../hooks/useGetOrders";
import PageWrapper from "../PageWrapper/PageWrapper";
import CONFIG from "../../config/config";
import { useSelector } from "react-redux";
import { Settings } from "../../store/reducers/AccountSlice";
import BrideHistoryWarningIcon from "../../components/BrideHistoryWarningIcon/BrideHistoryWarningIcon";
import { TooltipProvider } from "../../components/TooltipV2/TooltipV2";

const PENDING_ORDER_STATUSES: string[] = ['DRAFT', 'WAITING', 'REQUIRES_CHANGES'];

function WelcomePage() {

  const { t, i18n } = useTranslation();
  const { isLoadingOrders, externalOrders } = useGetOrders({
    shopId: '',
    date: '',
    hidden: false,
    external: true,
    statuses: PENDING_ORDER_STATUSES,
  });
  const navigate = useNavigate();
  const settings: Settings = useSelector((state: any) => state.settings.value);
  const [liveAds, setLiveAds] = useState<LiveHomepageAd[]>([]);
  const [isLoadingAds, setIsLoadingAds] = useState(true);
  const [selectedAd, setSelectedAd] = useState<LiveHomepageAd | null>(null);
  const recordedAdViews = useRef<Set<number>>(new Set());
  const [meetings, setMeetings] = useState<ReviewablePendingBookDto[]>([]);
  const [currentMonthSales, setCurrentMonthSales] = useState<{
    totalRevenue: number;
    orderCount: number;
    averageOrderValue: number;
    currency: string;
  } | null>(null);
  const [previousMonthSales, setPreviousMonthSales] = useState<{
    totalRevenue: number;
    orderCount: number;
    averageOrderValue: number;
    currency: string;
  } | null>(null);
  const [meetingsStats, setMeetingsStats] = useState<{
    currentWeek: number;
    previousWeek: number;
    difference: number;
  } | null>(null);
  const [noShowStats, setNoShowStats] = useState<{
    currentWeek: number;
    previousWeek: number;
    difference: number;
  } | null>(null);
  
  const [isLoadingSales, setIsLoadingSales] = useState<boolean>(true);
  const [isLoadingMeetingsStats, setIsLoadingMeetingsStats] = useState<boolean>(true);
  const [isLoadingNoShowStats, setIsLoadingNoShowStats] = useState<boolean>(true);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState<boolean>(false);
  const [loadingBookings, setLoadingBookings] = useState<Set<number>>(new Set());

  const handleBookingAction = async (id: number, action: 'accept' | 'reject') => {
    if (loadingBookings.has(id)) {
      return;
    }

    setLoadingBookings(prev => new Set(prev).add(id));
    try {
      await MeetingsAPI[action](id);
      setMeetings(prevMeetings => prevMeetings.filter(meeting => meeting.id !== id));
      // Trigger a re-fetch of meetings in the PendingBookingsButton
      const event = new CustomEvent('pendingMeetingsUpdated');
      window.dispatchEvent(event);
    } catch (error) {
      console.error(`Error ${action}ing booking:`, error);
    } finally {
      setLoadingBookings(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const formatDifference = (currentValue: number, previousValue: number) => {
    const difference = currentValue - previousValue;
    const isNegative = difference < 0;
    const formattedDifference = `${isNegative ? '-' : '+'}${Math.abs(difference).toFixed(2)}`;
    const colorClass = isNegative ? 'text-red-500' : 'text-green-500';
    const Icon = isNegative ? ArrowDown : ArrowUp;

    return (
      <span className={`flex items-center ${colorClass}`}>
        <Icon className="h-4 w-4 mr-1" />
        {formattedDifference}
      </span>
    );
  };

  useEffect(() => {
    let isMounted = true;
    const loadAds = async () => {
      try {
        const locale = (i18n.language || 'en').split('-')[0];
        const ads = await HomepageAdsAPI.listLive(locale);
        if (!isMounted) {
          return;
        }
        setLiveAds(ads);
      } catch (error) {
        console.error('Failed to load homepage ads', { error });
        if (isMounted) {
          setLiveAds([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingAds(false);
        }
      }
    };
    void loadAds();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const handleSlideVisible = useCallback((banner: BannerData) => {
    if (banner.type !== 'homead') {
      return;
    }
    const adId = Number(banner.id);
    if (!Number.isFinite(adId) || recordedAdViews.current.has(adId)) {
      return;
    }
    const ad = liveAds.find((item) => item.id === adId);
    if (!ad) {
      return;
    }
    recordedAdViews.current.add(adId);
    datadogRum.addAction('Homepage Ad - Impression', { adId: ad.id, campaignName: ad.name });
    void HomepageAdsAPI.recordView(ad.id).catch((error) => {
      recordedAdViews.current.delete(adId);
      console.error('Failed to record homepage ad view', { adId: ad.id, error });
    });
  }, [liveAds]);

  useEffect(() => {
    fetchMeetings();
    fetchOverviewData();
    fetchMeetingsStatistics();
    fetchNoShowStatistics();
    
    const timer = setTimeout(() => {
      setShowWelcomeMessage(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  const fetchOverviewData = async () => {
    setIsLoadingSales(true);
    try {
      const response = await StatisticsAPI.getOverview();
      const data = response.data;
      
      // Safe destructuring with defaults
      const currentMonth = data?.currentMonth || { orderCount: 0, profit: 0 };
      const previousMonth = data?.previousMonth || { orderCount: 0, profit: 0 };
      const currency = data?.currency || 'EUR';
      
      const { orderCount: currentOrderCount = 0, profit: currentProfit = 0 } = currentMonth;
      const { orderCount: prevOrderCount = 0, profit: prevProfit = 0 } = previousMonth;

      const currentAverageOrderValue = currentOrderCount !== 0 ? currentProfit / currentOrderCount : 0;
      const prevAverageOrderValue = prevOrderCount !== 0 ? prevProfit / prevOrderCount : 0;

      setCurrentMonthSales({
        totalRevenue: currentProfit,
        orderCount: currentOrderCount,
        averageOrderValue: currentAverageOrderValue,
        currency: currency,
      });

      setPreviousMonthSales({
        totalRevenue: prevProfit,
        orderCount: prevOrderCount,
        averageOrderValue: prevAverageOrderValue,
        currency: currency,
      });
    } catch (error) {
      console.error('Failed to fetch overview data:', error);
      // Set default values on error
      setCurrentMonthSales({
        totalRevenue: 0,
        orderCount: 0,
        averageOrderValue: 0,
        currency: 'EUR',
      });
      setPreviousMonthSales({
        totalRevenue: 0,
        orderCount: 0,
        averageOrderValue: 0,
        currency: 'EUR',
      });
    } finally {
      setIsLoadingSales(false);
    }
  };

  const fetchMeetings = async () => {
    try {
      const response = await MeetingsAPI.pendings();
      setMeetings(response.data.meetings);
    } catch (error) {
      console.error('Failed to fetch pending meetings:', error);
    }
  };

  const fetchMeetingsStatistics = async () => {
    setIsLoadingMeetingsStats(true);
    try {
      // Calculate date ranges for this week and last week using millisecond arithmetic
      const now = new Date();
      const oneDayMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      
      // Get start of current week (Sunday)
      const daysFromSunday = now.getDay();
      const currentWeekStart = new Date(now.getTime() - (daysFromSunday * oneDayMs));
      currentWeekStart.setHours(0, 0, 0, 0);
      
      // Get end of current week (Saturday)
      const currentWeekEnd = new Date(currentWeekStart.getTime() + (6 * oneDayMs));
      currentWeekEnd.setHours(23, 59, 59, 999);
      
      // Get start of previous week
      const previousWeekStart = new Date(currentWeekStart.getTime() - (7 * oneDayMs));
      
      // Get end of previous week
      const previousWeekEnd = new Date(currentWeekStart.getTime() - oneDayMs);
      previousWeekEnd.setHours(23, 59, 59, 999);
      
      const [currentWeekResponse, previousWeekResponse] = await Promise.all([
        MeetingsAPI.get(currentWeekStart.toISOString(), currentWeekEnd.toISOString()),
        MeetingsAPI.get(previousWeekStart.toISOString(), previousWeekEnd.toISOString())
      ]);
      
      const currentWeekCount = currentWeekResponse.data?.length || 0;
      const previousWeekCount = previousWeekResponse.data?.length || 0;
      const difference = currentWeekCount - previousWeekCount;
      
      setMeetingsStats({
        currentWeek: currentWeekCount,
        previousWeek: previousWeekCount,
        difference: difference
      });
    } catch (error) {
      console.error('Failed to fetch meetings statistics:', error);
      setMeetingsStats({
        currentWeek: 0,
        previousWeek: 0,
        difference: 0
      });
    } finally {
      setIsLoadingMeetingsStats(false);
    }
  };

  const fetchNoShowStatistics = async () => {
    setIsLoadingNoShowStats(true);
    try {
      // Calculate date ranges for this week and last week using millisecond arithmetic
      const now = new Date();
      const oneDayMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      
      // Get start of current week (Sunday)
      const daysFromSunday = now.getDay();
      const currentWeekStart = new Date(now.getTime() - (daysFromSunday * oneDayMs));
      currentWeekStart.setHours(0, 0, 0, 0);
      
      // Get end of current week (Saturday)
      const currentWeekEnd = new Date(currentWeekStart.getTime() + (6 * oneDayMs));
      currentWeekEnd.setHours(23, 59, 59, 999);
      
      // Get start of previous week
      const previousWeekStart = new Date(currentWeekStart.getTime() - (7 * oneDayMs));
      
      // Get end of previous week
      const previousWeekEnd = new Date(currentWeekStart.getTime() - oneDayMs);
      previousWeekEnd.setHours(23, 59, 59, 999);
      
      const [currentWeekResponse, previousWeekResponse] = await Promise.all([
        MeetingsAPI.get(currentWeekStart.toISOString(), currentWeekEnd.toISOString()),
        MeetingsAPI.get(previousWeekStart.toISOString(), previousWeekEnd.toISOString())
      ]);
      
      // Filter for no-show meetings
      const currentWeekNoShows = currentWeekResponse.data?.filter(meeting => meeting.noShow)?.length || 0;
      const previousWeekNoShows = previousWeekResponse.data?.filter(meeting => meeting.noShow)?.length || 0;
      const difference = currentWeekNoShows - previousWeekNoShows;
      
      setNoShowStats({
        currentWeek: currentWeekNoShows,
        previousWeek: previousWeekNoShows,
        difference: difference
      });
    } catch (error) {
      console.error('Failed to fetch no-show statistics:', error);
      setNoShowStats({
        currentWeek: 0,
        previousWeek: 0,
        difference: 0
      });
    } finally {
      setIsLoadingNoShowStats(false);
    }
  };

  const pendingOrders = externalOrders.filter(order => order.status === "DRAFT" || order.status === "WAITING" || order.status === "REQUIRES_CHANGES");

  const OrderSkeleton = () => (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div className="space-y-2">
            <Skeleton className="h-4 w-[150px]" />
            <Skeleton className="h-3 w-[100px]" />
          </div>
          <Skeleton className="h-8 w-[80px]" />
        </div>
      ))}
    </div>
  );

  return (
    <PageWrapper>
      <>
        <div className="min-h-[75vh]">
        <div className="container mx-auto space-y-6 pt-5">
          {isLoadingAds && liveAds.length === 0 ? (
            <CardV2>
              <Skeleton
                data-testid="homepage-ads-skeleton"
                className="min-h-[280px] w-full rounded-lg md:min-h-[400px]"
              />
            </CardV2>
          ) : liveAds.length > 0 ? (
            <CardV2 className="bg-primary text-primary-foreground">
              <BannerSlider
                banners={liveAds.map((ad) => ({
                  id: String(ad.id),
                  type: 'homead' as const,
                  title: ad.title,
                  imageUrl: ad.imageUrl,
                  primaryAction: {
                    label: ad.ctaText,
                    onClick: () => setSelectedAd(ad),
                  },
                }))}
                autoPlay={true}
                interval={6000}
                showNavigation={true}
                showArrows={true}
                onSlideVisible={handleSlideVisible}
              />
            </CardV2>
          ) : null}

          <div className={`${showWelcomeMessage ? 'animate-fade-in-delicate' : 'opacity-0'}`}>
            <h1 className="text-2xl font-bold text-foreground">
              {t("home.welcome.title", "Welcome back!")}
            </h1>
            <p className="text-muted-foreground">
              {t("home.welcome.message", "Here's what's happening with your business today.")}
            </p>
          </div>

          {/* Action Panels Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Register New Sale Panel */}
            <PermissionFlags requiredFlags={['order-add']}>
              <CardV2>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PlusCircle className="h-5 w-5" />
                    {t("home.section.sales.title", "Register New Sale")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <PrimaryButton
                    text={t("home.section.sales.cta", "Register New Sale")!}
                    fn={() => navigate('/calendar?openSalesFlow=true')}
                  />
                </CardContent>
              </CardV2>
            </PermissionFlags>

            {/* Pending Orders Panel */}
            <PermissionFlags requiredFlags={['see-orders']}>
              <CardV2>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {t("home.section.pending.title")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-[100px] overflow-y-auto pr-2">
                    {isLoadingOrders ? (
                      <OrderSkeleton />
                    ) : pendingOrders.length > 0 ? (
                      <ul className="space-y-4">
                        {pendingOrders.map((order) => (
                         <li
                         key={order.id}
                         className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted rounded-lg"
                          >
                            <div className="space-y-1 mb-2 sm:mb-0">
                              <p className="font-medium text-sm">
                                {order.b2bType === "OUT" ? order.receiverShopName : order.requesterShopName}
                              </p>
                              <h3 className="font-semibold text-base">{order.itemName}</h3>
                              <p className="text-sm">{order.clientName}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(order.inStoreDate).toLocaleDateString()}
                              </p>
                            </div>
                            <PrimaryButton 
                              text={t("home.section.pending.cta")!}
                              fn={() => navigate(`/orders?partnersOrders=true&orderId=${order.id}`)}
                            />
                       </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        No pending orders
                      </div>
                    )}
                  </div>
                </CardContent>
              </CardV2>
            </PermissionFlags>
            
            {/* Pending Bookings Panel */}
            <PermissionFlags requiredFlags={['create-meetings']}>
              <CardV2>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarDays className="h-5 w-5" />
                    {t("home.section.bookings.title")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-[100px] overflow-y-auto pr-2">
                    {meetings.length > 0 ? (
                      <ul className="space-y-4">
                        {meetings.map((booking) => (
                          <li
                            key={booking.id}
                            className="flex items-center justify-between p-3 bg-muted rounded-lg"
                          >
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                {booking.customerName}
                                {booking.brideHistoryWarning && (
                                  <TooltipProvider delayDuration={200}>
                                    <BrideHistoryWarningIcon reason={booking.brideHistoryWarningReason} />
                                  </TooltipProvider>
                                )}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {booking.date} at {booking.time}
                              </p>
                            </div>
                            <div>
                              {loadingBookings.has(booking.id) ? (
                                <div className="flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-600"></div>
                                </div>
                              ) : (
                                <>
                                  <PlainTextButton 
                                    text={t("booking.pending.accept")!} 
                                    onClick={() => handleBookingAction(booking.id, "accept")}
                                  />
                                  &nbsp;
                                  <PlainTextButton
                                    text={t("booking.pending.reject")!}
                                    onClick={() => handleBookingAction(booking.id, "reject")}
                                  />
                                </>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        No pending bookings
                      </div>
                    )}
                  </div>
                  <CardDescription className="mt-4 text-center">
                    <Link text={t("home.section.bookings.footer.setup.1")} url="/bookings" /> {t("home.section.bookings.footer.setup.2")}{' '}
                    <Link text={t("home.section.bookings.footer.setup.3")} url="/calendar" />.
                  </CardDescription>
                </CardContent>
              </CardV2>
            </PermissionFlags>

          </div>

          {/* Sales Statistics */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Total Revenue Card */}
            <AdminRestricted>
              <CardV2>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    {t("home.section.stats.revenue.title")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingSales ? (
                    <Skeleton className="h-8 w-full" />
                  ) : currentMonthSales && previousMonthSales ? (
                    <>
                      <p className="text-2xl font-bold">
                      { CONFIG.SUPPORTED_CURRENCIES_SYMBOLS[currentMonthSales.currency as keyof typeof CONFIG.SUPPORTED_CURRENCIES_SYMBOLS] ?? CONFIG.SUPPORTED_CURRENCIES_SYMBOLS[settings?.currency as keyof typeof CONFIG.SUPPORTED_CURRENCIES_SYMBOLS] ?? '€' } {currentMonthSales.totalRevenue.toLocaleString()}
                      </p>
                      <p className="text-sm">
                        {formatDifference(
                          currentMonthSales.totalRevenue,
                          previousMonthSales.totalRevenue
                        )}
                      </p>
                    </>
                  ) : (
                    <p className="text-2xl font-bold">N/A</p>
                  )}
                  <CardDescription>{t("home.section.stats.revenue.comparison")}</CardDescription>
                </CardContent>
              </CardV2>
            </AdminRestricted>

            {/* Order Count Card */}
            <AdminRestricted>
              <CardV2>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    {t("home.section.stats.orders.title")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingSales ? (
                    <Skeleton className="h-8 w-full" />
                  ) : currentMonthSales && previousMonthSales ? (
                    <span className="inline">
                      <p className="text-2xl font-bold">{currentMonthSales.orderCount}</p>
                      <p className="text-sm">
                        {formatDifference(currentMonthSales.orderCount, previousMonthSales.orderCount)}
                      </p>
                    </span>
                  ) : (
                    <p className="text-2xl font-bold">N/A</p>
                  )}
                  <CardDescription>{t("home.section.stats.revenue.comparison")}</CardDescription>
                </CardContent>
              </CardV2>
            </AdminRestricted>

            {/* Meetings Statistics Card */}
            <PermissionFlags requiredFlags={['create-meetings']}>
              <CardV2>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    {t("home.section.stats.meetings.title", "Meetings This Week")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingMeetingsStats ? (
                    <Skeleton className="h-8 w-full" />
                  ) : meetingsStats ? (
                    <>
                      <p className="text-2xl font-bold">{meetingsStats.currentWeek}</p>
                      <p className="text-sm">
                        {formatDifference(meetingsStats.currentWeek, meetingsStats.previousWeek)}
                      </p>
                    </>
                  ) : (
                    <p className="text-2xl font-bold">N/A</p>
                  )}
                  <CardDescription>{t("home.section.stats.meetings.comparison", "vs last week")}</CardDescription>
                </CardContent>
              </CardV2>
            </PermissionFlags>

            <PermissionFlags requiredFlags={['create-meetings']}>
              <CardV2>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    {t("home.section.stats.noshow.title", "No-Shows This Week")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingNoShowStats ? (
                    <Skeleton className="h-8 w-full" />
                  ) : noShowStats ? (
                    <>
                      <p className="text-2xl font-bold">{noShowStats.currentWeek}</p>
                      <p className="text-sm">
                        {formatDifference(noShowStats.currentWeek, noShowStats.previousWeek)}
                      </p>
                    </>
                  ) : (
                    <p className="text-2xl font-bold">N/A</p>
                  )}
                  <CardDescription>{t("home.section.stats.noshow.comparison", "vs last week")}</CardDescription>
                </CardContent>
              </CardV2>
            </PermissionFlags>
          </div>
        </div>
      </div>
      <HomeAdModal ad={selectedAd} onClose={() => setSelectedAd(null)} />
      </>
    </PageWrapper>
  );
}

export default WelcomePage;
